import { create } from 'zustand';
import type { Workflow } from '@n8n-builder/n8n-types';
import type { Conversation, Message as ApiMessage, ConversationFull, DebugLog } from '@n8n-builder/api-client';
import { createApiClient } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../lib/api-config';
import { CACHE_TTL, fetchWithCache } from '../utils/cache';

interface Message {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  workflow?: Workflow;
  workflowId?: string;
  plan?: WorkflowPlan;
}

export interface ToolCall {
  id: string;
  tool: string;
  input: unknown;
  result?: unknown;
  status: 'running' | 'complete';
  iteration: number;
}

export interface StreamingState {
  thinking: string | null;
  toolCalls: ToolCall[];
  currentIteration: number;
}

export interface PlanStep {
  stepNumber: number;
  nodeType: string;
  nodeName: string;
  description: string;
  configuration?: string;
  resource?: string;
  operation?: string;
}

export interface WorkflowPlan {
  id: string;
  title: string;
  summary: string;
  steps: PlanStep[];
  requiredCredentials?: string[];
  connections?: string;
}

export interface AttachedWorkflow {
  filename: string;
  workflow: Workflow;
  nodeCount: number;
}

export interface QuestionOption {
  id: string;
  label: string;
  description?: string;
}

export interface Question {
  id: string;
  question: string;
  type: 'select' | 'text';
  options?: QuestionOption[];
  placeholder?: string;
  required?: boolean;
}

export interface UserQuestions {
  id: string;
  questions: Question[];
}

export type ChatPhase = 'idle' | 'planning' | 'reviewing' | 'building' | 'answering';

interface ChatState {
  // Current conversation
  conversationId: string | null;
  messages: Message[];
  isLoading: boolean;
  currentWorkflow: Workflow | null;
  currentWorkflowId: string | null;

  // Plan state
  currentPhase: ChatPhase;
  currentPlan: WorkflowPlan | null;

  // Question state
  currentQuestions: UserQuestions | null;

  // Attachment state
  attachedWorkflow: AttachedWorkflow | null;

  // Streaming state
  streamingState: StreamingState;

  // Debug logs (per conversation)
  debugLogs: Record<string, DebugLog[]>;

  // Conversation list
  conversations: Conversation[];
  isLoadingConversations: boolean;
  conversationsLastFetchedAt: number | null;

  // Loading state for conversation switching
  loadingConversationId: string | null;

  // Actions
  setConversationId: (id: string | null) => void;
  setConversations: (conversations: Conversation[]) => void;
  setIsLoadingConversations: (loading: boolean) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversationInList: (id: string, updates: Partial<Conversation>) => void;
  removeConversation: (id: string) => void;
  addMessage: (message: Message) => void;
  setLoading: (loading: boolean) => void;
  setCurrentWorkflow: (workflow: Workflow | null, workflowId?: string | null) => void;
  clearMessages: () => void;
  startNewConversation: () => void;
  loadConversation: (id: string, messages: ApiMessage[], workflows?: Map<string, Workflow>) => void;
  loadConversationFast: (data: ConversationFull) => void;
  setLoadingConversationId: (id: string | null) => void;
  getHistory: () => Array<{ role: 'user' | 'assistant'; content: string }>;

  // Plan actions
  setCurrentPlan: (plan: WorkflowPlan | null) => void;
  setCurrentPhase: (phase: ChatPhase) => void;
  clearPlan: () => void;

  // Question actions
  setCurrentQuestions: (questions: UserQuestions | null) => void;
  clearQuestions: () => void;

  // Attachment actions
  setAttachedWorkflow: (attachment: AttachedWorkflow | null) => void;
  clearAttachedWorkflow: () => void;

  // Streaming actions
  setThinking: (content: string | null) => void;
  addToolCall: (tool: string, input: unknown, iteration: number) => void;
  completeToolCall: (tool: string, iteration: number, result: unknown) => void;
  clearStreamingState: () => void;

  // Debug log actions
  addDebugLog: (conversationId: string, log: DebugLog) => void;
  clearDebugLogs: (conversationId: string) => void;
  getDebugLogs: (conversationId: string) => DebugLog[];

  // Cache actions
  fetchConversations: (options?: { force?: boolean }) => void;
  invalidateConversations: () => void;
}

const initialStreamingState: StreamingState = {
  thinking: null,
  toolCalls: [],
  currentIteration: 0,
};

export const useChatStore = create<ChatState>((set, get) => ({
  // State
  conversationId: null,
  messages: [],
  isLoading: false,
  currentWorkflow: null,
  currentWorkflowId: null,
  currentPhase: 'idle',
  currentPlan: null,
  currentQuestions: null,
  attachedWorkflow: null,
  streamingState: { ...initialStreamingState },
  debugLogs: {},
  conversations: [],
  isLoadingConversations: false,
  conversationsLastFetchedAt: null,
  loadingConversationId: null,

  // Actions
  setConversationId: (conversationId) => set({ conversationId }),

  setConversations: (conversations) => set({ conversations, conversationsLastFetchedAt: Date.now() }),

  setIsLoadingConversations: (isLoadingConversations) => set({ isLoadingConversations }),

  addConversation: (conversation) =>
    set((state) => ({
      conversations: [conversation, ...state.conversations],
      conversationsLastFetchedAt: null,
    })),

  updateConversationInList: (id, updates) =>
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    })),

  removeConversation: (id) =>
    set((state) => ({
      conversations: state.conversations.filter((c) => c.id !== id),
      conversationsLastFetchedAt: null,
      // Clear current conversation if deleted
      ...(state.conversationId === id
        ? { conversationId: null, messages: [], currentWorkflow: null, currentWorkflowId: null, currentPlan: null, currentQuestions: null, currentPhase: 'idle' as ChatPhase }
        : {}),
    })),

  addMessage: (message) =>
    set((state) => {
      const newMessages = [...state.messages, message];
      return {
        messages: newMessages,
        currentWorkflow: message.workflow ?? state.currentWorkflow,
        currentWorkflowId: message.workflowId ?? state.currentWorkflowId,
        currentPlan: message.plan ?? state.currentPlan,
        // If a plan was added, switch to reviewing phase
        currentPhase: message.plan ? 'reviewing' : state.currentPhase,
      };
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setCurrentWorkflow: (currentWorkflow, currentWorkflowId) => set({ currentWorkflow, currentWorkflowId: currentWorkflowId ?? null }),

  clearMessages: () => set({ messages: [], currentWorkflow: null, currentWorkflowId: null, currentPlan: null, currentQuestions: null, attachedWorkflow: null, currentPhase: 'idle' }),

  startNewConversation: () =>
    set({
      conversationId: null,
      messages: [],
      currentWorkflow: null,
      currentWorkflowId: null,
      currentPlan: null,
      currentQuestions: null,
      attachedWorkflow: null,
      currentPhase: 'idle',
    }),

  loadConversation: (id, apiMessages, workflows) => {
    const messages: Message[] = apiMessages.map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      workflowId: msg.workflowId || undefined,
      workflow: msg.workflowId && workflows ? workflows.get(msg.workflowId) : undefined,
      plan: msg.planJson ? JSON.parse(msg.planJson) : undefined,
    }));

    // Find the last message with a workflow in the conversation
    const lastWorkflowMessage = messages
      .slice()
      .reverse()
      .find((m) => m.workflow);

    // Find the last message with a plan in the conversation
    const lastPlanMessage = messages
      .slice()
      .reverse()
      .find((m) => m.plan);

    // Determine phase: if workflow exists, idle; if only plan exists, reviewing
    const hasWorkflow = !!lastWorkflowMessage?.workflow;
    const hasPlan = !!lastPlanMessage?.plan;
    const phase: ChatPhase = hasWorkflow ? 'idle' : (hasPlan ? 'reviewing' : 'idle');

    set({
      conversationId: id,
      messages,
      currentWorkflow: lastWorkflowMessage?.workflow || null,
      currentWorkflowId: lastWorkflowMessage?.workflowId || null,
      currentPlan: lastPlanMessage?.plan || null,
      currentQuestions: null,
      currentPhase: phase,
    });
  },

  loadConversationFast: (data) => {
    const messages: Message[] = data.messages.map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      workflowId: msg.workflowId || undefined,
      // Only attach the workflow to the message that owns the latest workflow
      workflow: (msg.workflowId && data.latestWorkflowId && msg.workflowId === data.latestWorkflowId && data.latestWorkflow)
        ? data.latestWorkflow
        : undefined,
      plan: msg.planJson ? JSON.parse(msg.planJson) : undefined,
    }));

    const lastPlanMessage = messages
      .slice()
      .reverse()
      .find((m) => m.plan);

    const hasWorkflow = !!data.latestWorkflow;
    const hasPlan = !!lastPlanMessage?.plan;
    const phase: ChatPhase = hasWorkflow ? 'idle' : (hasPlan ? 'reviewing' : 'idle');

    set({
      conversationId: data.id,
      messages,
      currentWorkflow: data.latestWorkflow || null,
      currentWorkflowId: data.latestWorkflowId || null,
      currentPlan: lastPlanMessage?.plan || null,
      currentQuestions: null,
      currentPhase: phase,
      loadingConversationId: null,
    });
  },

  setLoadingConversationId: (loadingConversationId) => set({ loadingConversationId }),

  getHistory: () =>
    get().messages.map((m) => ({
      role: m.role,
      content: m.content,
    })),

  // Plan actions
  setCurrentPlan: (currentPlan) => set({ currentPlan }),

  setCurrentPhase: (currentPhase) => set({ currentPhase }),

  clearPlan: () => set({ currentPlan: null, currentPhase: 'idle' }),

  // Question actions
  setCurrentQuestions: (currentQuestions) => set({ currentQuestions }),

  clearQuestions: () => set({ currentQuestions: null, currentPhase: 'idle' }),

  // Attachment actions
  setAttachedWorkflow: (attachedWorkflow) => set({ attachedWorkflow }),

  clearAttachedWorkflow: () => set({ attachedWorkflow: null }),

  // Streaming actions
  setThinking: (content) =>
    set((state) => ({
      streamingState: {
        ...state.streamingState,
        thinking: content,
      },
    })),

  addToolCall: (tool, input, iteration) =>
    set((state) => ({
      streamingState: {
        ...state.streamingState,
        currentIteration: iteration,
        toolCalls: [
          ...state.streamingState.toolCalls,
          {
            id: `${tool}-${iteration}-${Date.now()}`,
            tool,
            input,
            status: 'running' as const,
            iteration,
          },
        ],
      },
      // Set phase to planning when we start tool calls
      currentPhase: state.currentPhase === 'idle' ? 'planning' : state.currentPhase,
    })),

  completeToolCall: (tool, iteration, result) =>
    set((state) => ({
      streamingState: {
        ...state.streamingState,
        toolCalls: state.streamingState.toolCalls.map((tc) =>
          tc.tool === tool && tc.iteration === iteration
            ? { ...tc, status: 'complete' as const, result }
            : tc
        ),
      },
    })),

  clearStreamingState: () =>
    set({
      streamingState: { ...initialStreamingState },
    }),

  // Debug log actions
  addDebugLog: (conversationId, log) =>
    set((state) => ({
      debugLogs: {
        ...state.debugLogs,
        [conversationId]: [...(state.debugLogs[conversationId] || []), log],
      },
    })),

  clearDebugLogs: (conversationId) =>
    set((state) => ({
      debugLogs: {
        ...state.debugLogs,
        [conversationId]: [],
      },
    })),

  getDebugLogs: (conversationId) => get().debugLogs[conversationId] || [],

  // Cache actions
  fetchConversations: (options) => {
    const state = get();
    fetchWithCache(
      state.conversations.length > 0,
      state.conversationsLastFetchedAt,
      CACHE_TTL.conversations,
      async () => {
        if (state.conversations.length === 0) {
          set({ isLoadingConversations: true });
        }
        try {
          const apiClient = createApiClient(API_BASE_URL);
          const result = await apiClient.getConversations();
          if (result.data?.conversations) {
            set({ conversations: result.data.conversations, conversationsLastFetchedAt: Date.now() });
          }
        } catch (error) {
          console.error('Failed to fetch conversations:', error);
        } finally {
          set({ isLoadingConversations: false });
        }
      },
      options,
    );
  },

  invalidateConversations: () => set({ conversationsLastFetchedAt: null }),
}));
